import React, { useState } from 'react';
import { X, Bookmark, Copy, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { SUGGESTED_PROMPTS, PROMPT_CATEGORIES, CATEGORY_COLORS } from '../data/suggestedPrompts';
import type { SavedPrompt } from '../hooks/useSavedPrompts';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';

type TabId = 'suggested' | 'yours';

interface PromptGalleryProps {
  open: boolean;
  onClose: () => void;
  savedPrompts: SavedPrompt[];
  onUsePrompt: (prompt: string) => void;
  onSavePrompt: (title: string, prompt: string, category?: string) => void;
  onRemoveSaved: (id: string) => void;
  isSaved: (promptText: string) => boolean;
}

export function PromptGallery({
  open,
  onClose,
  savedPrompts,
  onUsePrompt,
  onSavePrompt,
  onRemoveSaved,
  isSaved,
}: PromptGalleryProps) {
  const [activeTab, setActiveTab] = useState<TabId>('suggested');
  const [categoryFilter, setCategoryFilter] = useState<string>('All');

  const handleCopy = (prompt: string) => {
    navigator.clipboard.writeText(prompt);
    toast.success('Copied to clipboard');
  };

  const suggestedFiltered =
    categoryFilter === 'All'
      ? SUGGESTED_PROMPTS
      : SUGGESTED_PROMPTS.filter((p) => p.category === categoryFilter);
  const savedFiltered =
    categoryFilter === 'All'
      ? savedPrompts
      : savedPrompts.filter((p) => (p.category ?? '') === categoryFilter);

  // The Dialog root stays mounted and `open` drives visibility, so Radix can
  // run its close sequence (focus restore to the trigger). Radix also owns
  // Escape and the focus trap that the old hand-rolled overlay lacked.
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        overlayClassName="backdrop-blur-xs"
        className="flex max-h-[80vh] w-[calc(100%-2rem)] max-w-2xl flex-col gap-0 overflow-hidden rounded border-border bg-surface-raised p-0 shadow-lg sm:max-w-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0 bg-linear-to-b from-surface-sunken/30 to-transparent">
          <div>
            <DialogTitle className="text-lg font-medium text-foreground flex items-center gap-2">
              {activeTab === 'suggested' ? (
                <>
                  <Sparkles className="w-5 h-5 text-primary" aria-hidden />
                  Suggested prompts
                </>
              ) : (
                <>
                  <Bookmark className="w-5 h-5 text-primary fill-primary/30" aria-hidden />
                  Your prompts
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs text-text-tertiary mt-0.5">
              {activeTab === 'suggested'
                ? 'Click a prompt to use it, or save your favorites for quick access'
                : 'Prompts you’ve saved from messages and suggestions'}
            </DialogDescription>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2.5 rounded-lg hover:bg-surface-hover text-text-tertiary hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" strokeWidth={2} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pt-3 shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab('suggested')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'suggested'
                ? 'bg-primary/20 text-primary shadow-sm'
                : 'text-text-tertiary hover:text-foreground hover:bg-surface-hover/60'
            }`}
          >
            <Sparkles className={`w-4 h-4 ${activeTab === 'suggested' ? 'text-primary' : ''}`} />
            Suggested
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('yours')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'yours'
                ? 'bg-primary/20 text-primary shadow-sm'
                : 'text-text-tertiary hover:text-foreground hover:bg-surface-hover/60'
            }`}
          >
            <Bookmark className={`w-4 h-4 ${activeTab === 'yours' ? 'text-primary' : ''}`} />
            Your prompts
          </button>
        </div>

        {/* Category filter + count */}
        <div className="flex items-center gap-3 px-5 py-3 shrink-0 border-b border-border/60">
          <label htmlFor="prompt-category-filter" className="text-xs font-medium text-text-tertiary shrink-0">
            Category
          </label>
          <select
            id="prompt-category-filter"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="text-sm bg-surface-raised text-foreground hover:bg-surface-hover/50 border border-border rounded-lg px-3 py-2 focus:outline-hidden focus:ring-2 focus:ring-primary/50 cursor-pointer flex-1 max-w-[200px]"
          >
            {PROMPT_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <span className="text-xs text-text-tertiary shrink-0">
            {activeTab === 'suggested' ? suggestedFiltered.length : savedFiltered.length} prompt{(activeTab === 'suggested' ? suggestedFiltered.length : savedFiltered.length) !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Prompt cards grid */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid gap-2.5 sm:grid-cols-2">
            {activeTab === 'suggested' &&
              suggestedFiltered.map((p) => (
                <PromptCard
                  key={p.id}
                  id={p.id}
                  title={p.title}
                  prompt={p.prompt}
                  description={p.description}
                  category={p.category}
                  saved={isSaved(p.prompt)}
                  onUse={() => {
                    onUsePrompt(p.prompt);
                    onClose();
                  }}
                  onCopy={() => handleCopy(p.prompt)}
                  onSave={() => onSavePrompt(p.title, p.prompt, p.category)}
                  onRemove={() => {
                    const sp = savedPrompts.find((s) => s.prompt === p.prompt);
                    if (sp) onRemoveSaved(sp.id);
                  }}
                  isSuggested
                />
              ))}
            {activeTab === 'yours' &&
              (savedFiltered.length === 0 ? (
                <div className="col-span-2 py-16 px-6 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-linear-to-br from-primary/15 to-primary/5 mb-5">
                    <Bookmark className="w-8 h-8 text-primary" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-sm font-medium text-foreground">No saved prompts yet</h3>
                  <p className="text-xs text-text-tertiary mt-2 max-w-xs mx-auto leading-relaxed">
                    Save prompts from the Suggested tab, or use the bookmark icon when you hover over your messages to add them here.
                  </p>
                </div>
              ) : (
                savedFiltered.map((p) => (
                  <PromptCard
                    key={p.id}
                    id={p.id}
                    title={p.title}
                    prompt={p.prompt}
                    saved
                    onUse={() => {
                      onUsePrompt(p.prompt);
                      onClose();
                    }}
                    onCopy={() => handleCopy(p.prompt)}
                    onRemove={() => onRemoveSaved(p.id)}
                    isSuggested={false}
                    category={p.category}
                  />
                ))
              ))}
          </div>
        </div>

        {/* Footer hint */}
        <div className="px-5 py-2.5 border-t border-border/60 shrink-0 bg-surface-sunken/20">
          <p className="text-[11px] text-text-tertiary">
            Click a prompt to use it • Copy to clipboard • <kbd className="px-1.5 py-0.5 rounded-md bg-surface-sunken/80 font-mono text-[10px] text-foreground/70">Esc</kbd> to close
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface PromptCardProps {
  id: string;
  title: string;
  prompt: string;
  description?: string;
  category?: string;
  saved: boolean;
  onUse: () => void;
  onCopy: () => void;
  onSave?: () => void;
  onRemove?: () => void;
  isSuggested: boolean;
}

function PromptCard({
  id,
  title,
  prompt,
  description,
  category,
  saved,
  onUse,
  onCopy,
  onSave,
  onRemove,
  isSuggested,
}: PromptCardProps) {
  const isCommand = prompt.startsWith('/');
  // The card must not be a button itself: it contains the Copy/Save buttons,
  // and interactive controls nested in an interactive control are unreachable
  // for AT (axe: nested-interactive). The title is the keyboard "use" control;
  // the card-wide onClick is a mouse-only convenience on top of it.
  return (
    <div
      data-testid={`prompt-card-${id}`}
      className="group p-4 rounded-lg border border-border bg-surface-raised hover:bg-primary/4 hover:border-primary/40 hover:shadow-md transition-all duration-200 cursor-pointer"
      onClick={onUse}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-medium text-foreground">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onUse();
                }}
                className="rounded text-left focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring/50"
              >
                {title}
              </button>
            </h3>
            {category && category !== 'All' && (() => {
              const styles = CATEGORY_COLORS[category] ?? { bg: 'bg-surface-sunken/80', text: 'text-text-tertiary' };
              return (
                <span className={`text-[10px] px-2 py-0.5 rounded-md font-medium ${styles.bg} ${styles.text}`}>
                  {category}
                </span>
              );
            })()}
          </div>
          {description && (
            <p className="text-xs text-text-tertiary mt-1 line-clamp-2">
              {description}
            </p>
          )}
          {isCommand && (
            <code className="inline-block mt-1.5 text-[11px] text-primary/90 font-mono bg-primary/10 px-2 py-0.5 rounded-md">
              {prompt}
            </code>
          )}
        </div>
        <div className="flex items-center gap-0.5 shrink-0 opacity-80 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={onCopy}
            className="p-2 rounded-lg hover:bg-primary/10 text-text-tertiary hover:text-primary transition-colors"
            title="Copy to clipboard"
            aria-label="Copy to clipboard"
          >
            <Copy className="w-4 h-4" strokeWidth={2} />
          </button>
          {isSuggested ? (
            saved ? (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  onRemove?.();
                }}
                className="p-2 rounded-lg hover:bg-destructive-subtle text-primary hover:text-destructive-text transition-colors"
                title="Remove from Your prompts"
                aria-label="Remove from saved"
              >
                <Bookmark className="w-4 h-4 fill-current" strokeWidth={2} />
              </button>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  onSave?.();
                }}
                className="p-2 rounded-lg hover:bg-primary/10 text-text-tertiary hover:text-primary transition-colors"
                title="Add to Your prompts"
                aria-label="Save prompt"
              >
                <Bookmark className="w-4 h-4" strokeWidth={2} />
              </button>
            )
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onRemove?.();
              }}
              className="p-2 rounded-lg hover:bg-destructive-subtle text-text-tertiary hover:text-destructive-text transition-colors"
              title="Remove from Your prompts"
              aria-label="Remove"
            >
              <Trash2 className="w-4 h-4" strokeWidth={2} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
